/**
 * A sent form's screen, drawn for the Mini App's `/form-request/:token` and the phone website's
 * form page alike (shared-client-screens D2, D3; rows F1 to F8). Props only: the app's
 * FormRequestPage and the website's FormRequestWebPage are its data parts over
 * `useFormRequestFlow`. Visual authority: docs/mockups/client-dashboard-redesign/form-request-flow.html.
 *
 * - `onBack`: the pinned Back, when the entry has somewhere to go back to (F1, F2); none for a
 *   form opened from a Telegram message in the app.
 * - `onHome` (Back to Home, F7) and `onRetry` (Try again, which reloads the page on both, F3).
 * - Owned here: the mount scroll reset (F4), the viewport variables, the native keyboard scroll
 *   held off, the keyboard-aware scroll of the fields, and the scroll lock. Not here (D2):
 *   Telegram's safe area and the `data-client-theme` effect stay in the app's data part (F5).
 */
import { useLayoutEffect, useRef } from 'react';
import { FlowBackButton } from '@/components/booking/FlowBackButton';
import { useClientLock } from '@/hooks/useClientLock';
import { useKeyboardAwareScroll } from '@/hooks/useKeyboardAwareScroll';
import { usePreventNativeKeyboardScroll } from '@/hooks/usePreventNativeKeyboardScroll';
import { useAppViewportCssVars } from '@/hooks/useVisualViewportCssVars';
import type { FieldError, FormDefinition, FormResponseInput } from '@/types/forms';
import { FormRequestForm, FormRequestLoading, TerminalScreen, type PageState } from './formRequestParts';
import styles from './FormRequestPage.module.css';

export interface FormRequestScreenProps {
  readonly status: PageState;
  readonly form: FormDefinition | null;
  readonly isAnonymous: boolean;
  readonly serverErrors: FieldError[];
  /** The last submit failed without ending the request. */
  readonly submitFailed?: boolean;
  /** The pinned Back; none when the entry has nowhere to go back to. */
  readonly onBack?: () => void;
  readonly onSubmit: (responses: FormResponseInput[]) => void;
  /** Every change, the whole answer set. */
  readonly onAnswersChange: (answers: Record<string, unknown>) => void;
  readonly onHome: () => void;
  readonly onRetry: () => void;
}

export function FormRequestScreen({
  status, form, isAnonymous, serverErrors, submitFailed, onBack, onSubmit, onAnswersChange, onHome, onRetry,
}: FormRequestScreenProps) {
  // Scroll to top on mount (WKWebView reset; see feedback_wkwebview_keyboard_scroll_fix).
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useAppViewportCssVars();
  usePreventNativeKeyboardScroll();
  const scrollRef = useRef<HTMLDivElement>(null);
  useKeyboardAwareScroll(scrollRef, { bottomPadding: 24 });
  useClientLock();

  if (status === 'form' || status === 'submitting') {
    if (!form) return null;
    return (
      <FormRequestForm
        form={form}
        isAnonymous={isAnonymous}
        submitting={status === 'submitting'}
        serverErrors={serverErrors}
        submitFailed={submitFailed}
        back={onBack ? <FlowBackButton className={styles.headBack} onClick={onBack} /> : undefined}
        scrollRef={scrollRef}
        onSubmit={onSubmit}
        onAnswersChange={onAnswersChange}
      />
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.atmosphere} aria-hidden="true" />
      {status === 'loading' ? (
        <FormRequestLoading />
      ) : (
        <TerminalScreen state={status} formName={form?.title} onHome={onHome} onRetry={onRetry} />
      )}
    </div>
  );
}
